import { ComplexValue, Messages, PromptConfirmation, withDropdownMenu, Link, ButtonTertiary } from "ui";
import styles from './change-case-status.scss';
import { CodeItemValueWire } from "app/components/options-handler/types";
import { MdArrowDropDown, MdArrowLeft, MdArrowRight } from "react-icons/md";
import React from "react";
import { mapStatusCode } from "app/area/case/features/calls-to-actions/mappings";
import { SpinLoader } from "app/components/spin-loader";
import { AutoCloseDuration, toastFactory } from "app/infrastructure/notifications/toast-factory";
import { ChangeCaseStatusWireData } from "app/area/case/features/calls-to-actions/types";
import { DropdownMenuContext } from "ui/common/dropdown-menu/types";
import { FailedDomainConstraintMetaData, UserMessage } from "app/types-shared";
import { getUserMessage } from "app/infrastructure/notifications/user-messages/get-user-message";
import * as navigate from 'app/area/shared/navigation';
import { routeConstants } from "app/routes/route-constants";
import { toAbsoluteUrl } from "app/infrastructure/hosting";
import { dropdownRefContextMenu, isCaseViewModalOpen, isMobileScreen, isWindowLargeEnough } from "app/components/helpers";

interface ChangeCaseStatusTranslationProps {
  textChangeStatus?: string;
  textWarningModalTitle?: string;
  textWarningModalDescription?: string;
  textWarningModalContinue?: string;
  textWarningModalEditCase?: string;
  textChangeStatusSuccess?: string;
  textChangeStatusWithCaseId?: string;
  textChangeStatusForReload?: string;
  textChangeStatusValidationError?: string;
}
interface ChangeCaseStatusProps extends ChangeCaseStatusTranslationProps {
  caseId: number;
  changeCaseStatusOptions: ChangeCaseStatusWireData;
  changeCaseStatus: (caseId: number, status: ComplexValue<number>, allowWarnings?: boolean) => 
    {userMessages: string | UserMessage[]; warnings: FailedDomainConstraintMetaData[]; validationErrors: string[]};
  reloadOnChangeStatus?: boolean;
  allowWarnings?: boolean;
  onContinueWarningChangeStatus?: () => void;
}

const CHANGE_STATUS_PARAM = 'changestatus';
const CHANGE_STATUS_WARNING_PARAM = 'warning';
const toasts = toastFactory();

export const handleChangeStatusUrlParams = (props: any, searchParams: URLSearchParams, setSearchParams: any) => {
  const statusParam = searchParams.get(CHANGE_STATUS_PARAM);
  const warning = searchParams.get(CHANGE_STATUS_WARNING_PARAM);
  if (statusParam) {
    if (warning === 'false') {
      toasts.dispatchSuccess(
        `${props.textChangeStatusWithCaseId} ${props.caseId} ${props.textChangeStatusSuccess} ${statusParam}${'.'}`);
    }
    searchParams.delete(CHANGE_STATUS_PARAM);
    searchParams.delete(CHANGE_STATUS_WARNING_PARAM);
    setSearchParams(searchParams);
  }
};

export const ChangeCaseStatus = withDropdownMenu((props: DropdownMenuContext & ChangeCaseStatusProps) => {
  const [warningsChangeStatus, setWarningsChangeStatus] = React.useState(undefined);
  const [changingStatus, setChangingStatus] = React.useState(false);
  const [nextValidStatus, setValidNextStatus] = React.useState(null);
  const reloadCurrentLocation = (statusDescription: string, warning: boolean) => {
    const urlParams = new URLSearchParams(location.search);
    urlParams.set(CHANGE_STATUS_PARAM, statusDescription);
    urlParams.set(CHANGE_STATUS_WARNING_PARAM, warning.toString());
    location.href = `${window.location.pathname}?${urlParams.toString()}`;
  };

  const onChangeStatus = async (e: React.MouseEvent<HTMLElement>, nextStatus: ComplexValue<number>, allowWarnings: boolean = true,
    forceReloadOnChangeStatus: boolean = false
  ) => 
  {
    e?.stopPropagation();
    const {textChangeStatusWithCaseId, caseId, textChangeStatusSuccess, textChangeStatusForReload} = props;
    const isCurrentStatusSelected = props.changeCaseStatusOptions?.currentStatus?.v === nextStatus?.value;
    if (isCurrentStatusSelected) {
      return;
    }

    setChangingStatus(true);
    const changeStatus = await props.changeCaseStatus?.(props.caseId, nextStatus, allowWarnings);

    if(changeStatus?.userMessages || !!changeStatus?.validationErrors?.length) {
      const toastErrorMessage = toastFactory({ autoClose: AutoCloseDuration.Longer });
      if(!!changeStatus?.userMessages) {
        toastErrorMessage.dispatchError(getUserMessage(changeStatus.userMessages));
      }
      
      else if(!!changeStatus?.validationErrors?.length) {
        toastErrorMessage.dispatchError(`${props.textChangeStatusWithCaseId} ${props.caseId} ${props.textChangeStatusValidationError}`);
        setWarningsChangeStatus(changeStatus.warnings);
      }
      setChangingStatus(false);
      return;
    }
    
    if (!allowWarnings && !!changeStatus?.warnings?.length && !forceReloadOnChangeStatus) {
      setWarningsChangeStatus(changeStatus.warnings);
    }
    else {
      if(props.reloadOnChangeStatus) {
        if(isCaseViewModalOpen()) {
          const urlParams = new URLSearchParams(location.search);
          urlParams.set('status', nextStatus?.description);
          const newQuery = Object.fromEntries(urlParams.entries());
          navigate.toCaseViewModal(props.caseId, newQuery);
          toasts.dispatchSuccess(`${textChangeStatusWithCaseId} ${caseId} ${textChangeStatusSuccess} ${nextStatus.description}`);
          setChangingStatus(false);
          props.handleDropdownHide();
          return;
        }
        reloadCurrentLocation(nextStatus?.description, false);
      }
      else {
        toasts.dispatchSuccess(`${textChangeStatusWithCaseId} ${caseId} ${textChangeStatusSuccess} ${nextStatus.description}${'.'}
          ${textChangeStatusForReload}`);
        props.handleDropdownHide();
      }
      setValidNextStatus({ value: nextStatus?.value, description: nextStatus?.description });
    }
  };
  
  const onClickChangeStatus = (validNextStatus: CodeItemValueWire, allowWarnings: boolean = true) => 
    (event: React.MouseEvent<HTMLButtonElement>) => {
    
    const mappedValidNextStatus = mapStatusCode(validNextStatus);
    setValidNextStatus(mappedValidNextStatus);
    onChangeStatus(event, mappedValidNextStatus, allowWarnings);
  };

  const onCancelWarningChangeStatus = () => {
    setWarningsChangeStatus(undefined);
    if(props.allowWarnings) {
      if(props.reloadOnChangeStatus) { 
        reloadCurrentLocation(nextValidStatus?.description, true);
        return;
      }
      toasts.dispatchInfo(props.textChangeStatusForReload);
    }  
    props.handleDropdownHide();
  };

  const mappedWarningsChangeStatus = () => warningsChangeStatus?.map((warning: FailedDomainConstraintMetaData, index: number) => {
    const message: Messages= {key: 0, label: '', message: ''};
    message.key = index;
    message.label = warning.domainText;
    message.message = warning.message;
    return message;
  }
);

  const getDescriptionChangeStatus = () => {
    const {textChangeStatusWithCaseId, caseId, textChangeStatusSuccess, textWarningModalDescription, allowWarnings} = props;
    const messageStatusChange = allowWarnings && 
      `${textChangeStatusWithCaseId} ${caseId} ${textChangeStatusSuccess} ${nextValidStatus.description}${'. '}`;

    return nextValidStatus?.description ? `${messageStatusChange ? messageStatusChange +'\n' : ''}${textWarningModalDescription}` : 
    `${textWarningModalDescription}`;
  };

  const onContinueWarningChangeStatus = async(e?: React.MouseEvent<HTMLElement>) => {
    if(props.allowWarnings) {
      navigate.toCaseEdit(props.caseId);
      return;
    }
    else {
      onChangeStatus(e, nextValidStatus, true, props.reloadOnChangeStatus);
    }
  };

  const editCaseButton = 
    <Link url={toAbsoluteUrl(routeConstants.caseEditFor(props.caseId))} target = '_self'>
      <ButtonTertiary onClick={props.handleDropdownHide}>
        {props.textWarningModalEditCase}
      </ButtonTertiary>
    </Link>;
  
  return <>
    {changingStatus && <SpinLoader />}
    {changingStatus && !!warningsChangeStatus?.length && !props.allowWarnings &&
      <PromptConfirmation
        title={props.textWarningModalTitle}
        description={`${getDescriptionChangeStatus()}`}
        confirmMessages={mappedWarningsChangeStatus()}
        confirmationStyle={'warning'}
        continueText={props.textWarningModalContinue}
        onContinue={onContinueWarningChangeStatus}
        closeOnDone={false}
        onCancel={onCancelWarningChangeStatus}
        btnExtraFooterActionOverride={!props.allowWarnings && editCaseButton}
      />}
    <li className={styles.nestedDropdown}>
      <div className="dropdown-item" tabIndex={0}>
        <span>{props.textChangeStatus}</span>
        <div className={styles.icon}>
          {isMobileScreen() ? <MdArrowDropDown className={`dropdown-arrow ${styles.navItemIcon}`}/> : 
            isWindowLargeEnough() ? <MdArrowRight className={`dropdown-arrow ${styles.navItemIcon}`} /> : 
            <MdArrowLeft className={`dropdown-arrow ${styles.navItemIcon}`} />}
        </div>
      </div>
      {<ul className={`dropdown-menu ${styles.subMenu}`} ref={dropdownRefContextMenu}>
        {props.changeCaseStatusOptions?.validNextStatuses?.map((validNextStatus: CodeItemValueWire, index: number) => {
          const isStatusItemSelected = props.changeCaseStatusOptions.currentStatus.v === validNextStatus.v;
          const currentStatusStyle = isStatusItemSelected ? styles.currentStatus : '';
          return (
            <li key={index}>
              <button id="nav-refresh-cache-button" className={`dropdown-item ${currentStatusStyle}`} type="button"
                onClick={onClickChangeStatus(validNextStatus, props.allowWarnings)}>
                {validNextStatus.d}
              </button>
            </li>
          );
        })}
      </ul>}
    </li>
  </>;
});
